import { createContext, memo, useContext, useMemo, useState } from 'react';
import { noop } from '@cbhq/cds-utils/noop';

import useLocalStorage, { SetValue } from ':cloud/hooks/useLocalStorage';
import { ProjectWithRole } from ':cloud/types/service_proto';
import { Project } from ':cloud/types/ts_types';

export const MAX_MEMBER_INVITE_LIMIT = 100;

type ProjectContextType = {
  selectedProjectId: string | null;
  setSelectedProjectId: SetValue<string | null>;
  selectedProject: Project | ProjectWithRole | null;
  setSelectedProject: SetValue<Project | ProjectWithRole | null>;
  hasInvitedMaxUsers: boolean;
};

const ProjectContext = createContext<ProjectContextType>({
  selectedProjectId: null,
  setSelectedProjectId: noop,
  selectedProject: null,
  setSelectedProject: noop,
  hasInvitedMaxUsers: false,
});

export const ProjectProvider = memo(function ProjectProvider({ children }) {
  const [selectedProjectId, setSelectedProjectId] = useLocalStorage<string | null>(
    'selected-project-id',
    null,
  );

  const [selectedProject, setSelectedProject] = useState<Project | ProjectWithRole | null>(null);

  // Necessary until project based team management is fully rolled out (useIsProjectTeamsEnabled)
  const isProjectWithRole = (
    project: Project | ProjectWithRole | null,
  ): project is ProjectWithRole => {
    return Boolean(project && 'invites' in project);
  };

  const hasInvitedMaxUsers = isProjectWithRole(selectedProject)
    ? selectedProject.invites.length >= MAX_MEMBER_INVITE_LIMIT
    : false;

  const contextData: ProjectContextType = useMemo(
    () => ({
      selectedProjectId,
      setSelectedProjectId,
      selectedProject,
      setSelectedProject,
      hasInvitedMaxUsers,
    }),
    [
      selectedProjectId,
      setSelectedProjectId,
      selectedProject,
      setSelectedProject,
      hasInvitedMaxUsers,
    ],
  );

  return <ProjectContext.Provider value={contextData}>{children}</ProjectContext.Provider>;
});

/**
 * DO NOT manually set `selectedProject`.
 * If you want to change the selected project, update the `projectId` param in the URL
 * and let the `useProjectNavigation` hook handle the rest.
 */
export function useProject() {
  return useContext(ProjectContext);
}
